import { defineStore } from "pinia";
import { ApiLocationsGet, ApiVesselsGet, ApiFavouritesGet, ApiFavouritesDelete, ApiFavouritesPost } from "@/lib/api";
import { redirectToLogin } from "@maersk-global/shared-js";
import * as CONSTANTS from "@/lib/CONSTANTS";
import { shipments } from "@/store/shipments";
import { etaChanges } from "@/store/etaChanges";
import { unconfirmedBookings } from "@/store/unconfirmedBookings";
import { documents } from "@/store/documents";

export const filters = defineStore("filters", {
  state: () => ({
    vessels: [],
    fromLocations: [],
    toLocations: [],
    roles: [],
    selectedVessel: null,
    selectedFromLocation: null,
    selectedToLocation: null,
    selectedRole: null,
    activeFilter: [],
    savedFilters: [],
    showDeleteFilterError: false
  }),
  getters: {
    getGlobalFilters() {
      const filters = {};
      if (this.selectedFromLocation) {
        filters.locationFrom =
          this.selectedFromLocation.type === "COUNTRY"
            ? this.selectedFromLocation.countryCode
            : `${this.selectedFromLocation.locationCityName}~${this.selectedFromLocation.countryCode}`;
      }
      if (this.selectedToLocation) {
        filters.locationTo =
          this.selectedToLocation.type === "COUNTRY"
            ? this.selectedToLocation.countryCode
            : `${this.selectedToLocation.locationCityName}~${this.selectedToLocation.countryCode}`;
      }
      if (this.selectedVessel) {
        filters.vesselName = this.selectedVessel.longName;
      }
      if (this.selectedRole) {
        filters.customerPartyRoleCodes = this.selectedRole;
      }
      return filters;
    }
  },
  actions: {
    async getVessels() {
      if (this.vessels?.length > 0) {
        return;
      }
      try {
        const response = await ApiVesselsGet();
        const vesselsResponse = await response.json();
        this.vessels = vesselsResponse?.vessels
          .map(
            (vessel) =>
              vessel.vesselName &&
              vessel.vesselIMONumber &&
              vessel.callSign && {
                longName: vessel.vesselName,
                value: vessel.vesselName,
                imoNumber: vessel.vesselIMONumber,
                callSign: vessel.callSign,
                label: vessel.vesselName
              }
          )
          .filter((x) => x && x.label?.trim());
      } catch (e) {
        this.vessels = [];
      }
    },
    async getFromLocations(searchTerm) {
      this.fromLocations = await this.getAllLocations(searchTerm);
    },
    async getToLocations(searchTerm) {
      this.toLocations = await this.getAllLocations(searchTerm);
    },
    async getAllLocations(searchTerm) {
      const queries = [
        { pageSize: 20, countryName: searchTerm, type: "Country" },
        { pageSize: 20, cityName: searchTerm, type: "City" }
      ];

      const responses = await Promise.allSettled(queries.map((q) => ApiLocationsGet(q)));
      const fulfilled = responses.filter((r) => r.status === "fulfilled").map((r) => r.value);

      const locations = [];
      if (fulfilled.length) {
        const results = await Promise.allSettled(fulfilled.map((res) => res.json()));
        results.forEach((r) => {
          if (r.status === "fulfilled" && Array.isArray(r.value)) {
            locations.push(...r.value);
          }
        });
      }

      return this.prepareLocations(locations);
    },
    prepareLocations(locations) {
      return locations.map((location) => ({
        locationCityName: location.cityName,
        locationName: location.cityName
          ? location.cityName + (location.regionName ? ` (${location.regionName})` : ``) + `, ${location.countryName}`
          : location.countryName,
        value: location.cityName
          ? location.cityName + (location.regionName ? ` (${location.regionName})` : ``) + `, ${location.countryName}`
          : location.countryName,
        countryName: location.countryName,
        countryCode: location.countryCode,
        maerskGeoLocationId: location.maerskGeoLocationId,
        type: location.type,
        label: location.cityName
          ? location.cityName + (location.regionName ? ` (${location.regionName})` : ``) + `, ${location.countryName}`
          : location.countryName
      }));
    },
    async getRoles() {
      const roles = [
        { label: "Please Select", value: "" },
        { label: "Booked by", value: "1" },
        { label: "Transport Document Receiver", value: "22" },
        { label: "Invoice Party", value: "14" },
        { label: "Credit Party", value: "26" },
        { label: "Price Owner", value: "44" },
        { label: "Shipper", value: "3" },
        { label: "Consignee", value: "4" }
      ];
      this.roles = roles;
    },
    updateVessel(vessel) {
      if (vessel) {
        this.selectedVessel = vessel;
        this.removeFromSelectedFilter(-1, "Vessel");
        this.activeFilter.unshift({ label: "Vessel", value: vessel.label });
      } else {
        this.selectedVessel = null;
        this.removeFromActiveFilter("Vessel");
      }
    },
    updateFromLocation(location) {
      if (location) {
        this.selectedFromLocation = location;
        this.removeFromSelectedFilter(-1, "From");
        this.activeFilter.unshift({ label: "From", value: location.label });
      } else {
        this.selectedFromLocation = null;
        this.removeFromActiveFilter("From");
      }
    },
    updateToLocation(location) {
      if (location) {
        this.selectedToLocation = location;
        this.removeFromSelectedFilter(-1, "To");
        this.activeFilter.unshift({ label: "To", value: location.label });
      } else {
        this.selectedToLocation = null;
        this.removeFromActiveFilter("To");
      }
    },
    updateRole(role) {
      if (role) {
        this.selectedRole = role.value;
        this.removeFromSelectedFilter(-1, "Role");
        this.activeFilter.unshift({ label: "Role", value: role.label });
      } else {
        this.selectedRole = null;
        this.removeFromActiveFilter("Role");
      }
    },
    removeFromActiveFilter(label) {
      const index = this.activeFilter.findIndex((f) => f.label === label);
      if (index > -1) {
        this.activeFilter.splice(index, 1);
      }
    },
    removeFromSelectedFilter(index, key) {
      if (key) {
        index = this.activeFilter.findIndex((x) => x.label === key);
      }
      if (index > -1) {
        this.activeFilter.splice(index, 1);
      }
    },
    async saveFilter(filterName) {
      let filter = {
        filterName,
        vessel: this.selectedVessel,
        from: this.selectedFromLocation,
        to: this.selectedToLocation,
        role: this.selectedRole
      };
      filter = Object.keys(filter)
        .filter((key) => filter[key])
        .reduce(
          (obj, key) => ({
            ...obj,
            [key]: filter[key]
          }),
          {}
        );
      this.savedFilters.push(filter);
      const isNewFilter = this.savedFilters.length ? false : true;
      await ApiFavouritesPost(JSON.stringify(this.savedFilters), isNewFilter);
      await this.getSavedFilters(true);
    },
    async deleteFilter(index) {
      this.savedFilters?.splice(index, 1);
      try {
        if (this.savedFilters?.length) {
          await ApiFavouritesPost(JSON.stringify(this.savedFilters), false);
        } else {
          await ApiFavouritesDelete();
        }
      } catch (error) {
        this.updateDeleteFilterError(true);
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          redirectToLogin(`${CONSTANTS.APP_BASE}/`);
        }
      }
    },
    updateDeleteFilterError(value) {
      this.showDeleteFilterError = value;
    },
    clearFilters() {
      this.updateFromLocation(null);
      this.updateToLocation(null);
      this.updateVessel(null);
      this.updateRole(null);
    },
    resetTabs() {
      shipments().shipments = [];
      etaChanges().shipments = [];
      unconfirmedBookings().unconfirmedBookingsData = [];
      documents().documentsOnPage = [];
    },
    async getSavedFilters(refresh) {
      try {
        if (!refresh) {
          const response = await ApiFavouritesGet();
          const filtersResponse = await response.json();
          this.savedFilters = JSON.parse(filtersResponse.favouritesData)?.sort((a, b) =>
            a.filterName.localeCompare(b.filterName)
          );
        }
        let fromLocation, toLocation;
        this.savedFilters = this.savedFilters.map((filter, index) => {
          const updateFilter = {
            filterName: filter.filterName
          };
          const keys = Object.keys(filter);
          keys.forEach((key) => {
            switch (key) {
              case "vessel":
                updateFilter.vessel = {
                  ...filter[key],
                  label: filter[key].vesselName || filter[key].longName,
                  value: filter[key].vesselName || filter[key].longName
                };
                break;
              case "from":
                fromLocation = filter[key].cityName
                  ? filter[key].cityName +
                    (filter[key].regionName ? ` (${filter[key].regionName})` : ``) +
                    `, ${filter[key].countryName}`
                  : filter[key].countryName;
                updateFilter.from = {
                  ...filter[key],
                  label: fromLocation,
                  value: fromLocation
                };
                break;
              case "to":
                toLocation = filter[key].cityName
                  ? filter[key].cityName +
                    (filter[key].regionName ? ` (${filter[key].regionName})` : ``) +
                    `, ${filter[key].countryName}`
                  : filter[key].countryName;
                updateFilter.to = {
                  ...filter[key],
                  label: toLocation,
                  value: toLocation
                };
                break;
              case "role":
                updateFilter.role = {
                  role: filter[key],
                  label: this.roles.find((role) => role.value === filter[key])?.label,
                  value: filter[key]
                };
            }
          });
          return { id: index, ...updateFilter };
        });
      } catch (e) {
        this.savedFilters = [];
      }
    }
  }
});
